import type { SwitchOrganizationCommandResult } from "@app/commands/enterpriseCloud/switchOrganization";
import { useRouter } from "@core/Api/useRouter";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import type { OrganizationListItem } from "@ext/enterprise-cloud/GesCloudApi";
import initEnterpriseCloud from "@ext/enterprise-cloud/utils/initEnterpriseCloud";
import { useCallback, useRef, useState } from "react";

/** `failed`: the error is already shown to the user; `skipped`: another switch is in flight. */
export type SwitchOrganizationResult = SwitchOrganizationCommandResult | "failed" | "skipped";

/** A pick made while a switch is in flight is dropped: two overlapping switches remove each other's workspace. */
const useSwitchOrganization = () => {
	const { environment } = usePlatform();
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();
	const switchingRef = useRef(false);
	const [isSwitching, setIsSwitching] = useState(false);

	const switchOrganization = useCallback(
		async (organization: OrganizationListItem, onSwitched?: () => void): Promise<SwitchOrganizationResult> => {
			if (switchingRef.current) return "skipped";
			switchingRef.current = true;
			setIsSwitching(true);
			try {
				const res = await FetchService.fetch<SwitchOrganizationCommandResult>(
					apiUrlCreator.switchGesCloudOrganization(
						organization.organizationInfo.redirectUrl,
						organization.organizationInfo.id,
					),
				);
				if (!res.ok) return "failed";

				const result = await res.json();
				if (result !== "switched") return result;

				onSwitched?.();
				if (environment === "tauri") await initEnterpriseCloud(router, apiUrlCreator);
				return "switched";
			} finally {
				switchingRef.current = false;
				setIsSwitching(false);
			}
		},
		[environment, router],
	);

	return { switchOrganization, isSwitching };
};

export default useSwitchOrganization;
